// Takeoff Canvas 2.0 Track G — detection proposals with review state
// (TAKEOFF_CANVAS_2_ROADMAP.md G, UI brief §20, compatibility audit §13).
// A room the AI prefill extracted is a PROPOSAL on the existing TakeoffRooms
// row — DetectionStatus / DetectionSource / DetectionConfidence /
// DetectionProposalJson (db/21) — never a second geometry model. The
// estimator accepts, edits or rejects; high confidence is never approval.
// Symbol detection has no producer yet (takeoff-analysis extracts rooms and
// scale only); when one exists it uses the same columns on the drawn row.

export const DETECTION_STATUSES = Object.freeze({
  proposed: { key: "proposed", label: "AI proposal — review", tone: "warn" },
  accepted: { key: "accepted", label: "Accepted", tone: "ok" },
  rejected: { key: "rejected", label: "Rejected", tone: "bad" },
});
export const isProposedRoom = (room) => room?.DetectionStatus === "proposed";
export const isDetectedRoom = (room) => !!room?.DetectionSource;

export function parseProposal(room) {
  try { const v = room?.DetectionProposalJson ? JSON.parse(room.DetectionProposalJson) : null; return v && typeof v === "object" ? v : null; } catch { return null; }
}

// 0–1 → label; null → "not reported" (the producer did not score it).
export function confidenceLabel(c) {
  const v = c == null || c === "" ? null : +c;
  if (v == null || !Number.isFinite(v)) return { key: "unknown", label: "confidence not reported", pct: null };
  const pct = Math.round(v * 100);
  if (v >= 0.85) return { key: "high", label: `${pct}% · high`, pct };
  if (v >= 0.6) return { key: "medium", label: `${pct}% · medium`, pct };
  return { key: "low", label: `${pct}% · low`, pct };
}

// What the estimator changed since the proposal (name, dimensions, position,
// outline) — shown beside Accept so accepting an edited room is deliberate.
const near = (a, b, tol = 0.05) => (a == null && b == null) || (a != null && b != null && Math.abs(+a - +b) <= tol);
export function proposalDelta(room) {
  const p = parseProposal(room);
  if (!p) return { edited: false, changes: [], hasProposal: false };
  const changes = [];
  if (p.room_name != null && String(p.room_name) !== String(room.RoomName)) changes.push({ field: "name", from: p.room_name, to: room.RoomName });
  if (p.length_ft != null && !near(p.length_ft, room.LengthFt)) changes.push({ field: "length", from: +p.length_ft, to: room.LengthFt == null ? null : +room.LengthFt });
  if (p.width_ft != null && !near(p.width_ft, room.WidthFt)) changes.push({ field: "width", from: +p.width_ft, to: room.WidthFt == null ? null : +room.WidthFt });
  if (p.canvas_x != null && (!near(p.canvas_x, room.CanvasX) || !near(p.canvas_y, room.CanvasY))) changes.push({ field: "position", from: `${p.canvas_x}, ${p.canvas_y}`, to: `${room.CanvasX}, ${room.CanvasY}` });
  let pPts = null; try { pPts = p.polygon_json ? JSON.parse(p.polygon_json)?.points?.length ?? null : null; } catch { pPts = null; }
  let rPts = null; try { rPts = room.PolygonJson ? JSON.parse(room.PolygonJson)?.points?.length ?? null : null; } catch { rPts = null; }
  if (pPts != null && pPts !== rPts) changes.push({ field: "outline", from: `${pPts} corners`, to: rPts == null ? "rectangle" : `${rPts} corners` });
  return { edited: changes.length > 0, changes, hasProposal: true };
}

export const describeChange = (c) => `${c.field}: ${c.from ?? "—"} → ${c.to ?? "—"}`;
